import { useState, useEffect, useCallback } from 'react';
import { getRegistryUrls } from '../api';
import {
  RegistryCharacter,
  RegistrySceneProp,
  RegistryAct,
  RegistryGlobalSummary,
  RegistryEpisodeSummary,
} from '../types';
import { normalizeCharacters, normalizeSceneProps, normalizeActs, enrichEpisodes } from './registryParser';

export interface RegistryData {
  characters: RegistryCharacter[];
  sceneProps: RegistrySceneProp[];
  acts: RegistryAct[];
  globalSummary: RegistryGlobalSummary;
  episodes: RegistryEpisodeSummary[];
}

// Shared loader/parser for the script-summary "registry" (characters, scene/props,
// acts, global summary, per-episode summaries). Used by the script-summary page,
// the storyboard page's per-episode summary, and the project home page.
export function useRegistry(projectId?: string): {
  data: RegistryData | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
} {
  const [data, setData] = useState<RegistryData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const raw = await getRegistryUrls(projectId);
      const characters = normalizeCharacters(raw.characters);
      const sceneProps = normalizeSceneProps(raw.sceneprops);
      const acts = normalizeActs(raw.acts);
      const globalSummary = (raw.global ?? {}) as RegistryGlobalSummary;
      const episodes = enrichEpisodes(raw.episodes, characters, sceneProps);
      setData({ characters, sceneProps, acts, globalSummary, episodes });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { data, loading, error, reload };
}
